import React from 'react';
import { motion } from 'framer-motion';
import { calculateWeeklyRP } from '../../game/systems/labor';
import { getTechNode, TECH_BRANCHES, TECH_NODES } from '../../game/data/technology';
import { buttonHover, buttonTap } from '../../ui/animations';

export function LabDetailScreen({ state, dispatch }) {
  const unlockedTech = state.unlockedTech ?? [];
  const lab = state.rooms.find(r => r.id === 'room-lab');
  const scientists = state.workers.filter(w => w.type === 'scientist');
  const weeklyRP = calculateWeeklyRP(state.workers, lab);
  const labEquip = lab?.equipment || {};
  const equipBonus = scientists.some(w => w.assignedRoomId === 'room-lab')
    ? Object.values(labEquip).reduce((s, e) => s + (e?.installed ? (e.rpBonus || 0) : 0), 0)
    : 0;

  const activeResearchNode = state.activeResearch ? getTechNode(state.activeResearch.nodeId) : null;
  const weeksLeft = activeResearchNode && weeklyRP > 0
    ? Math.ceil((state.activeResearch.pointsRequired - state.activeResearch.pointsAccumulated) / weeklyRP)
    : null;

  const labEvents = state.activeEvents.filter(e => e.roomId === 'room-lab' && !e.resolved);
  const labFailure = labEvents.find(e => e.type === 'lab_equipment_failure');

  const EQUIPMENT_LIST = [
    { key: 'basic', label: 'Basic Bench', icon: '🧪', rpBonus: 0, cost: 0, alwaysInstalled: true },
    { key: 'microscope', label: 'Microscope', icon: '🔭', rpBonus: 3, cost: 3000 },
    { key: 'pcr', label: 'PCR Machine', icon: '⚗️', rpBonus: 8, cost: 8000 },
    { key: 'sequencer', label: 'Gene Sequencer', icon: '🧬', rpBonus: 15, cost: 20000 },
  ];

  // TECH_BRANCHES is an object keyed by branch ID; convert to array for iteration
  const branchList = Object.values(TECH_BRANCHES);
  const nodeList = Object.values(TECH_NODES);

  return (
    <div className="screen" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 14, height: '100%', overflow: 'hidden' }}>

      {/* LEFT — Equipment + Events */}
      <div style={{ overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>

        {/* Header */}
        <div className="panel">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 32 }}>🔬</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Research Lab</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Scientific research & development facility</div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--blue)' }}>{weeklyRP} RP/wk</div>
              {labFailure && <div style={{ fontSize: 11, color: 'var(--red)' }}>⚠️ Equipment failure — RP halved</div>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Banked RP </span><span style={{ fontWeight: 700, color: 'var(--blue)' }}>{state.researchPoints}</span></div>
            <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Scientists </span><span style={{ fontWeight: 700 }}>{scientists.filter(w => w.assignedRoomId === 'lab').length}/{scientists.length}</span></div>
            <div><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Equip bonus </span><span style={{ fontWeight: 700, color: 'var(--green)' }}>+{equipBonus} RP/wk</span></div>
          </div>
        </div>

        {/* Equipment panel */}
        <div className="panel">
          <div className="panel-title">Lab Equipment</div>
          {EQUIPMENT_LIST.map(item => {
            const isInstalled = item.alwaysInstalled || labEquip[item.key]?.installed;
            const canAfford = !isInstalled && state.cash >= item.cost;
            return (
              <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontSize: 20, width: 28 }}>{item.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {item.rpBonus > 0 ? `+${item.rpBonus} RP/wk` : 'Base equipment'}
                  </div>
                </div>
                {isInstalled ? (
                  <span style={{ fontSize: 12, color: 'var(--green)', fontWeight: 600 }}>Installed</span>
                ) : (
                  <motion.button
                    className="btn btn-primary btn-sm"
                    disabled={!canAfford}
                    onClick={() => dispatch({ type: 'BUY_LAB_EQUIPMENT', payload: { equipKey: item.key } })}
                    whileHover={canAfford ? buttonHover : {}}
                    whileTap={canAfford ? buttonTap : {}}
                  >
                    Buy ${item.cost.toLocaleString()}
                  </motion.button>
                )}
              </div>
            );
          })}
        </div>

        {/* Lab Events */}
        {labEvents.length > 0 && (
          <div className="panel" style={{ borderColor: 'var(--red)' }}>
            <div className="panel-title" style={{ color: 'var(--red)' }}>Lab Alerts</div>
            {labEvents.map(evt => (
              <div key={evt.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', marginBottom: 6 }}>
                <div>
                  <span style={{ marginRight: 6 }}>{evt.icon}</span>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{evt.label}</span>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{evt.description}</div>
                </div>
                <button
                  className="btn btn-warn btn-sm"
                  disabled={evt.resolutionCost > 0 && state.cash < evt.resolutionCost}
                  onClick={() => dispatch({ type: 'RESOLVE_EVENT', payload: { eventId: evt.id } })}
                >
                  {evt.resolutionCost > 0 ? `Fix $${evt.resolutionCost}` : 'Resolve'}
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Tech snapshot — read-only overview */}
        <div className="panel">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div className="panel-title" style={{ margin: 0 }}>Tech Overview</div>
            <button className="btn btn-secondary btn-sm" onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'research' } })}>
              Full Tree
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {branchList.map(branch => (
              <div key={branch.id} style={{ background: 'var(--border-light)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: branch.color, marginBottom: 4 }}>{branch.icon} {branch.name}</div>
                {nodeList.filter(n => n.branch === branch.id).map(node => {
                  const done = unlockedTech.includes(node.id);
                  const active = state.activeResearch?.nodeId === node.id;
                  return (
                    <div key={node.id} style={{ fontSize: 10, color: done ? 'var(--green)' : active ? 'var(--blue)' : 'var(--text-muted)', marginBottom: 2 }}>
                      {done ? 'done' : active ? 'active' : 'o'} {node.name}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT — Scientists + Active Research */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto' }}>

        {/* Active research */}
        <div className="panel">
          <div className="panel-title">Active Research</div>
          {state.activeResearch && activeResearchNode ? (
            <>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{activeResearchNode.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>{activeResearchNode.description}</div>
              <div style={{ height: 8, background: 'var(--border-light)', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
                <motion.div
                  style={{ height: '100%', background: 'var(--blue)', borderRadius: 4 }}
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, Math.round((state.activeResearch.pointsAccumulated / state.activeResearch.pointsRequired) * 100))}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)' }}>
                <span>{state.activeResearch.pointsAccumulated} / {state.activeResearch.pointsRequired} RP</span>
                <span>{weeksLeft !== null ? `~${weeksLeft} weeks` : weeklyRP === 0 ? 'No scientists assigned' : '...'}</span>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>🧪</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>No active research</div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'research' } })}
              >
                Start Research
              </button>
            </div>
          )}
        </div>

        {/* Scientist assignment */}
        <div className="panel">
          <div className="panel-title">Scientists</div>
          {scientists.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '8px 0' }}>
              No scientists on staff. Hire scientists in the Workforce tab.
              <button
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 8, display: 'block' }}
                onClick={() => dispatch({ type: 'NAVIGATE', payload: { screen: 'workforce' } })}
              >
                Go to Workforce
              </button>
            </div>
          ) : (
            scientists.map(w => {
              const assignedToLab = w.assignedRoomId === 'lab';
              const perSciRP = Math.round((10 + (w.skill / 100) * 15) * (w.morale / 100));
              return (
                <div key={w.id} style={{ borderBottom: '1px solid var(--border-light)', paddingBottom: 10, marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{w.name}</span>
                    <span style={{ fontSize: 11, color: assignedToLab ? 'var(--green)' : 'var(--text-muted)' }}>
                      {assignedToLab ? `+${perSciRP} RP/wk` : 'Not in lab'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Skill {w.skill}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Morale {Math.round(w.morale)}%</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>${w.currentWage}/wk</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <motion.button
                      className={`btn btn-sm ${assignedToLab ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ flex: 1 }}
                      onClick={() => dispatch({ type: assignedToLab ? 'UNASSIGN_WORKER' : 'ASSIGN_WORKER', payload: assignedToLab ? { workerId: w.id } : { workerId: w.id, roomId: 'lab' } })}
                      whileHover={buttonHover}
                      whileTap={buttonTap}
                    >
                      {assignedToLab ? 'In Lab' : '+ Assign to Lab'}
                    </motion.button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* RP breakdown */}
        <div className="panel">
          <div className="panel-title">RP Breakdown</div>
          <div className="stat-row">
            <span className="lbl">Scientists</span>
            <span className="val text-blue">{weeklyRP - equipBonus} RP/wk</span>
          </div>
          <div className="stat-row">
            <span className="lbl">Equipment</span>
            <span className="val text-green">+{equipBonus} RP/wk</span>
          </div>
          {labFailure && (
            <div className="stat-row">
              <span className="lbl" style={{ color: 'var(--red)' }}>Failure penalty</span>
              <span className="val" style={{ color: 'var(--red)' }}>x0.5</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, marginTop: 6 }} className="stat-row">
            <span className="lbl" style={{ fontWeight: 700 }}>Total</span>
            <span className="val" style={{ fontWeight: 700, color: 'var(--blue)' }}>{labFailure ? Math.floor(weeklyRP * 0.5) : weeklyRP} RP/wk</span>
          </div>
        </div>
      </div>
    </div>
  );
}
